import React, { useState, useEffect, useRef, RefObject, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import QRCode from "react-qr-code";

import { GenericModal } from "components/Modal/Modal";
import PreviousButton from "components/Previous/PreviousButton";
import { Button } from "components/Button";
import Loader from "components/Loaders/Loader/Loader"

import { Carousel as AntdCarousel, Col, Row } from "antd"
import { CarouselRef } from "antd/lib/carousel"
import { CaretLeftOutlined, CaretRightOutlined, CloseOutlined } from "@ant-design/icons";

import { cdn } from "core/cdn";
import { HOME_PAGE } from "core/constants";
import { languages } from "lib/languages";
import { ContactlessPaymentState } from "./services/reducer";
import { DeviceModel, contactlessPaymentActions } from "./services/actions";

import styles from "./ReadersConfiguration.module.scss";

const ITEMS_PER_CAROUSEL_PAGE = 6


type ReadersConfigurationState = 'download_mobile_app' | 'phone_association' | 'phone_associated' | 'delete_phone' | null;


const DownloadMobileAppView: React.FC<{ smartRedirectUrl: string, phoneAssociation: () => void }> = ({ smartRedirectUrl, phoneAssociation }) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '60px' }}>
            <div style={{
                color: '#7A7A7A',
                textAlign: 'center',
                fontSize: '34px',
                maxWidth: '1519px',
            }}>
                {languages.scanQRCodeToDownloadApp}
            </div>
            <div>
                <QRCode value={smartRedirectUrl} size={256} level="H" />
            </div>
            <Button type="primary" onClick={phoneAssociation}
                style={{
                    display: 'flex',
                    padding: '27px 30px',
                    alignItems: 'center',
                }}>
                {languages.iHaveDownloadedTheApp}
            </Button>
        </div>
    );
};


const PhoneAssociationView: React.FC<{}> = () => {
    const [startTime, setStartTime] = useState<number | null>(null)
    const [isExpired, setIsExpired] = useState<boolean>(false)
    const { readerQR } = useSelector(
        (state: { contactlessPayment: ContactlessPaymentState }) => state.contactlessPayment
    )
    const durationSec = typeof readerQR.duration === 'number' ? readerQR.duration : 0
    const QR_TOKEN_DURATION_MS = durationSec * 1000
    const [timeRemaining, setTimeRemaining] = useState<number>(QR_TOKEN_DURATION_MS)
    const dispatch = useDispatch()

    // Formater le temps restant en MM:SS
    const formatTime = (ms: number): string => {
        const totalSeconds = Math.floor(ms / 1000)
        const minutes = Math.floor(totalSeconds / 60)
        const seconds = totalSeconds % 60
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    // Récupérer le QR code
    const fetchQR = React.useCallback(() => {
        dispatch(contactlessPaymentActions.readerLinkQRRequest())
    }, [dispatch])

    // Regénérer le QR code
    const handleRegenerateQR = () => {
        setIsExpired(false)
        setTimeRemaining(QR_TOKEN_DURATION_MS)
        fetchQR()
    }

    // Réinitialiser le flag si le token est réinitialisé
    useEffect(() => {
        if (readerQR.token) {
            setStartTime(Date.now())
            setTimeRemaining(QR_TOKEN_DURATION_MS)
            setIsExpired(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [readerQR.token])

    // Timer countdown
    useEffect(() => {
        if (!startTime || !readerQR.token) return

        const interval = setInterval(() => {
            const elapsed = Date.now() - startTime
            const remaining = Math.max(0, QR_TOKEN_DURATION_MS - elapsed)
            setTimeRemaining(remaining)

            if (remaining === 0) {
                clearInterval(interval)
                setIsExpired(true)
            }
        }, 1000)

        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, readerQR.token])

    // Généreration du QR code
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!readerQR.token && !readerQR.loading && !readerQR.error) {
            fetchQR()
        }
    }, [readerQR.token, readerQR.loading, readerQR.error, fetchQR])

    useEffect(() => {
        if (isExpired) {
            handleRegenerateQR()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpired])

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '60px', minHeight: '650px' }}>
            <div style={{
                color: '#7A7A7A',
                textAlign: 'center',
                fontSize: '34px',
            }}>
                {languages.scanQRCodeWithContactlessPaymentApp}
            </div>
            <div>
                {readerQR.loading && !readerQR.token ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '60px' }}>
                        <Loader />
                        {languages.generatingQRCode}
                    </div>

                ) : readerQR.error ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '60px' }}>
                        {readerQR.error.text}
                        <Button onClick={fetchQR} type="primary">
                            {languages.retry}
                        </Button>
                    </div>
                ) : isExpired ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '600px',
                            height: '256px',
                        }}>
                            {languages.qrCodeExpired}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
                            {languages.timeRemaining} : 00:00
                        </div>
                        <div onClick={handleRegenerateQR}
                            style={{
                                color: '#1D71B8',
                                textAlign: 'center',
                                fontFamily: 'Now',
                                fontSize: '28px',
                                fontWeight: '400',
                                textDecoration: 'underline',
                            }}>
                            {languages.regenerateQRCode}
                        </div>
                    </div>

                ) : readerQR.token ? (
                    <div>
                        <QRCode value={readerQR.token} size={316} />
                    </div>
                ) : null}
            </div>
            {timeRemaining > 0 && !isExpired && !readerQR.loading && !readerQR.error && readerQR.token ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '90px' }}>
                    {languages.qrCodeRefreshIn} : {formatTime(timeRemaining)}
                </div>
            ) : null}
        </div>
    );
};


const PhoneAssociatedView: React.FC<{
    deviceModels, associateNewPhone: () => void,
    deletePhone: (model: DeviceModel) => void
}> = ({ deviceModels, associateNewPhone, deletePhone }) => {

    const dispatch = useDispatch()
    const carouselRef = useRef<CarouselRef>() as RefObject<CarouselRef>
    const [hideArrows, setHideArrows] = useState({ left: true, right: false })

    const handlePrevious = () => { carouselRef.current?.prev() }
    const handleNext = () => { carouselRef.current?.next() }

    // Carousel pages
    const splitIntoPages = (items: DeviceModel[], itemsPerPage: number): DeviceModel[][] => {
        const pages: DeviceModel[][] = []
        for (let i = 0; i < items.length; i += itemsPerPage) {
            pages.push(items.slice(i, i + itemsPerPage))
        }
        return pages
    }
    const pages = splitIntoPages(deviceModels.models, ITEMS_PER_CAROUSEL_PAGE)

    const onBeforeChange = (from: number, to: number): void => {
        if (to <= 0) {
            setHideArrows({ left: true, right: pages.length > 1 ? false : true })
        } else {
            setHideArrows({ left: false, right: to === pages.length - 1 ? true : false })
        }
    }

    // Gérer le clic sur le bouton de suppression
    const handleDeleteClick = (model: DeviceModel) => {
        deletePhone(model)
    }

    // Définir les flèches de navigation
    useEffect(() => {
        if (deviceModels.models.length <= ITEMS_PER_CAROUSEL_PAGE) {
            setHideArrows({ left: true, right: true })
        } else {
            setHideArrows({ left: true, right: false })
        }
    }, [deviceModels.models.length])

    // Refresh automatique des readers toutes les 5 secondes
    useEffect(() => {
        const interval = setInterval(() => {
            dispatch(contactlessPaymentActions.readerGetDeviceModelsRequest())
        }, 5000)
        return () => clearInterval(interval)
    }, [dispatch])

    return (
        <div style={{
            display: 'flex',
            width: '1789px',
            maxHeight: '650px',
            flexDirection: 'column',
            alignItems: 'center',
        }}>
            <Row className={styles.readersCarouselContainer}>
                <Col
                    className={styles.carouselArrow}
                    span={2}
                    style={{
                        textAlign: "right",
                        visibility: hideArrows.left ? "hidden" : "visible",
                        pointerEvents: hideArrows.left ? "none" : "auto"
                    }}>
                    <CaretLeftOutlined
                        onClick={handlePrevious}
                        className={styles.carouselArrowIcon}
                    />
                </Col>
                <Col span={20} style={{ textAlign: "center" }}>
                    {pages.length > 0 && (
                        <AntdCarousel
                            ref={carouselRef}
                            beforeChange={onBeforeChange}
                            dots={false}
                        >
                            {pages.map((page, pageIndex) => (
                                <div key={pageIndex}>
                                    <div className={styles.readersGrid}>
                                        {page.map((model, index) => (
                                            <div key={`${model.reader_uuid}-${index}`} style={{ width: '100%' }}>
                                                <div className={styles.readerCard}>
                                                    <div
                                                        style={{
                                                            position: 'absolute',
                                                            right: '16px',
                                                            top: '16px',
                                                        }}>
                                                        <CloseOutlined style={{ fontSize: '40px', cursor: 'pointer', color: 'red' }} onClick={() => handleDeleteClick(model)} />
                                                    </div>
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        gap: '10px',
                                                        height: '100%',
                                                    }}>
                                                        <div style={{
                                                            color: '#211452',
                                                            textAlign: 'center',
                                                            fontFamily: 'Now',
                                                            fontSize: '28px',
                                                            fontWeight: '400',
                                                            lineHeight: 'normal',
                                                            wordBreak: 'break-word',
                                                        }}>
                                                            {model.name}
                                                        </div>
                                                        <div>
                                                            {
                                                                model.enabled ? (
                                                                    <span className={styles.readerCardEnabled}>
                                                                        activé
                                                                    </span>
                                                                ) : (
                                                                    <span className={styles.readerCardDisabled}>
                                                                        désactivé
                                                                    </span>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </AntdCarousel>
                    )}
                </Col>
                <Col
                    className={styles.carouselArrow}
                    span={2}
                    style={{
                        textAlign: "left",
                        visibility: hideArrows.right ? "hidden" : "visible",
                        pointerEvents: hideArrows.right ? "none" : "auto"
                    }}
                >
                    <CaretRightOutlined
                        className={styles.carouselArrowIcon}
                        onClick={handleNext}
                    />
                </Col>
            </Row>
            <div className={styles.actionButtonContainer}>
                <Button
                    type="primary"
                    onClick={associateNewPhone}
                >
                    {languages.associateNewPhone.toUpperCase()}
                </Button>
            </div>
        </div>
    );
};


const DeletePhoneView: React.FC<{
    modelToDelete: DeviceModel | null,
    phoneAssociation: () => void,
    isDeleting: boolean,
    onDeleteStart: () => void
}> = ({ modelToDelete, phoneAssociation, isDeleting, onDeleteStart }) => {
    const dispatch = useDispatch()

    const handleConfirmDelete = () => {
        if (modelToDelete) {
            onDeleteStart();
            dispatch(contactlessPaymentActions.readerDeleteDeviceModelRequest(modelToDelete))
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '40px' }}>
            <div style={{
                display: 'flex',
                color: '$primary',
                textAlign: 'center',
                fontSize: '48px',
                fontWeight: '500',
            }}>
                {languages.deleteDeviceModelWarning}
            </div>
            <div style={{
                display: 'flex',
                color: '$primary',
                textAlign: 'center',
                fontSize: '48px',
                fontWeight: '700',
            }}>
                {modelToDelete?.name}
            </div>
            <div style={{ display: 'flex', gap: '40px', marginTop: '40px' }}>
                <Button style={{ width: '500px', height: '100px' }} onClick={() => phoneAssociation()}>
                    {languages.cancel}
                </Button>
                <Button
                    danger
                    type="primary"
                    onClick={handleConfirmDelete}
                    style={{
                        width: '500px',
                        height: '100px',
                    }}>
                    {languages.delete}
                </Button>
            </div>
        </div>
    );
};


const ReadersConfiguration = () => {
    const SMART_REDIRECT_URL = "https://www.medadom.com/redirect/terminal/store/";
    const navigate = useNavigate();
    const { deviceModels } = useSelector(
        (state: { contactlessPayment: ContactlessPaymentState }) => state.contactlessPayment
    )
    const dispatch = useDispatch()

    const getInitialState = (): ReadersConfigurationState => {
        // Si aucun device model n'est trouvé, afficher la vue de téléchargement de l'application
        if (deviceModels.models.length === 0) {
            return 'download_mobile_app';
        } else {
            return 'phone_associated';
        }
    };

    const [currentState, setCurrentState] = useState<ReadersConfigurationState>(getInitialState());
    const [modalVisible, setModalVisible] = useState(true);
    const [deviceToDelete, setDeviceToDelete] = useState<DeviceModel | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const lengthWhenEnteredAssociationRef = useRef<number>(0);

    // Définir l'état initial
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        const notAllowedStates = ['phone_association', 'delete_phone'];
        if (!deviceModels.loading && !notAllowedStates.includes(currentState)) {
            const newState = getInitialState();
            setCurrentState(newState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceModels.models.length, deviceModels.loading, currentState]);

    // Charger les device models au montage
    useEffect(() => {
        dispatch(contactlessPaymentActions.readerGetDeviceModelsRequest())
    }, [dispatch])

    // Update models length on phone association page
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (currentState === 'phone_association') {
            const interval = setInterval(() => {
                dispatch(contactlessPaymentActions.readerGetDeviceModelsPollRequest());
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [currentState, dispatch]);

    // Mémoriser le nombre de readers à l'entrée sur la page d'association (pour ne rediriger que si un nouveau a été lié)
    useEffect(() => {
        if (currentState === 'phone_association') {
            lengthWhenEnteredAssociationRef.current = deviceModels.models.length;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentState]);

    // Rediriger au jumelage
    useEffect(() => {
        const length = deviceModels.models.length;
        if (
            currentState === 'phone_association' &&
            length > 0 &&
            length > lengthWhenEnteredAssociationRef.current
        ) {
            setCurrentState('phone_associated');
        }
    }, [deviceModels.models.length, currentState]);

    // Écouter le succès/échec de la suppression
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (isDeleting && currentState === 'delete_phone') {
            if (!deviceModels.loading) {
                if (!deviceModels.error) {
                    setDeviceToDelete(null);

                    if (deviceModels.models.length === 0) {
                        setCurrentState('download_mobile_app');
                    } else {
                        setCurrentState('phone_associated');
                    }
                } else {
                    console.error('Erreur lors de la suppression:', deviceModels.error);
                }
                setIsDeleting(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceModels.loading, deviceModels.error, isDeleting, currentState])

    // Afficher le contenu en fonction de l'état
    const renderContent = () => {
        switch (currentState) {
            case 'download_mobile_app':
                return <DownloadMobileAppView
                    smartRedirectUrl={SMART_REDIRECT_URL}
                    phoneAssociation={() => setCurrentState('phone_association')}
                />;
            case 'phone_association':
                return <PhoneAssociationView
                />;
            case 'phone_associated':
                return <PhoneAssociatedView
                    deviceModels={deviceModels}
                    associateNewPhone={() => setCurrentState('phone_association')}
                    deletePhone={(model: DeviceModel) => {
                        setDeviceToDelete(model);
                        setCurrentState('delete_phone');
                        setIsDeleting(false);
                    }}
                />;
            case 'delete_phone':
                return <DeletePhoneView
                    modelToDelete={deviceToDelete}
                    isDeleting={isDeleting}
                    onDeleteStart={() => setIsDeleting(true)}
                    phoneAssociation={() => {
                        if (!isDeleting) {
                            setDeviceToDelete(null);
                            setCurrentState('phone_associated');
                        }
                    }}
                />;
            default:
                return null;
        }
    };


    // Définir le titre en fonction de l'état
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const modalTitle = useMemo(() => {
        const titleText = (() => {
            switch (currentState) {
                case "download_mobile_app":
                    return languages.downloadMedadomPaymentTerminalApp;
                case "phone_association":
                    return languages.associatePaymentTerminal;
                case "phone_associated":
                    return languages.associateKioskForContactlessPayment;
                case "delete_phone":
                    return (
                        <img
                            src={cdn("/icons/warning_icon.svg")}
                            alt="warning"
                            style={{
                                width: "160px",
                                height: "160px",
                                flexShrink: 0,
                                marginBottom: "50px",
                            }}
                        />
                    );
                default:
                    return "";
            }
        })();

        return (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                <span
                    style={{
                        color: "#211452",
                        fontSize: "48px",
                        fontWeight: "500",
                        lineHeight: "normal",
                    }}
                >
                    {titleText}
                </span>
            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentState,
        languages,
    ]);

    // Gérer le clic sur le bouton précédent
    const handlePreviousClick = () => {
        if (currentState === 'phone_association' && deviceModels.models.length > 0) {
            setCurrentState('phone_associated');
        }
        else if (currentState === 'phone_association') {
            setCurrentState('download_mobile_app');
        } else if (currentState === 'delete_phone') {
            setCurrentState('phone_associated');
        } else {
            navigate(HOME_PAGE);
        }
    };

    // Gérer le clic sur le bouton de fermeture
    const handleCloseClick = () => {
        navigate(HOME_PAGE);
    };

    // Modal tempalte / base for all states
    return (
        <GenericModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            title={modalTitle}
            style={
                {
                    width: '1789px',
                    height: '861px'
                }
            }
            closable={false}
            footer={
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <PreviousButton
                        text={languages.previous}
                        onClick={handlePreviousClick}
                    />
                </div>
            }
        >
            <div style={{}}>
                <div style={{ position: 'absolute', top: '20px', right: '30px' }}>
                    <CloseOutlined style={{ fontSize: '40px', cursor: 'pointer', color: 'red' }}
                        onClick={() => handleCloseClick()}
                    />
                </div>
                {renderContent()}
            </div>
        </GenericModal>
    );
};


export default ReadersConfiguration;
